import { ThemeProvider } from '@mui/material/styles';
import * as dotenv from 'dotenv';
import React, { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import './App.css';

import DrawerContainer from './components/common/drawers/DrawerContainer';
import Intro from './components/common/Intro';
import LogoContainer from './components/common/LogoContainer';
import ModeToast from './components/common/ModeToast';
import Toolbar from './components/common/Toolbar/Toolbar';
import Header from './components/layout/Header';
// LoginModal import removed (it's in the provider now)
import FloorPlannerScene from './components/three/FloorPlannerScene';
import CompassWidget from './components/widgets/CompassWidget';
import ScaleWidget from './components/widgets/ScaleWidget';
import { AnnotationsProvider } from './provider/AnnotationsProvider';
import { BuildingProvider } from './provider/BuildingProvider/BuildingProvider';
import ConfirmationDialogProvider from './provider/ConfirmationDialogProvider';
import { DraggingProvider } from './provider/DraggingProvider';
import { DrawingProvider } from './provider/DrawingProvider';
import ExportProvider from './provider/ExportProvider';
import { HistoryProvider } from './provider/HistoryProvider';
import { useLanguage } from './provider/LanguageProvider';
import { LoginProvider, useLoginContext } from './provider/LoginProvider'; // IMPORT THIS
import { ModalProvider } from './provider/ModalProvider';
import { MultiSelectToggleProvider } from './provider/MultiSelectToggleProvider';
import { PlanProvider } from './provider/PlanProvider';
import { QueryParamsProvider } from './provider/QueryParamsProvider';
import RoomComponentsDataProvider from './provider/RoomComponentsDataProvider';
import { SceneDataProvider } from './provider/SceneDataProvider';
import { SelectionProvider } from './provider/SelectionProvider';
import { SingleSelectProvider } from './provider/SingleSelectProvider';
import { SnackbarProvider } from './provider/SnackbarProvider';
import ToolbarProvider from './provider/ToolbarProvider';
import { UnitProvider } from './provider/UnitProvider';
import UserDataProvider from './provider/UserDataProvider';
import { ValidationProvider } from './provider/ValidationProvider';
import { ZoomChangeProvider } from './provider/ZoomChangeProvider';

import { KeyboardShortcutProvider } from './provider/KeyboardShortcutProvider';
import { LoadingProvider } from './provider/LoadingProvider';
import theme from './theme';
import { createIntlInstance, intlErrorHandler } from './utils/localesSetup';

dotenv.config();

// Helper component to connect Context to Header
const AppContent = ({ showIntro, handleIntroEnd }: { showIntro: boolean; handleIntroEnd: () => void }) => {
  const { isLoginModalOpen, openLoginModal } = useLoginContext();

  return (
    <>
      {showIntro && <Intro onEnd={handleIntroEnd} />}
      <Header onLoginClick={openLoginModal} />
      <SingleSelectProvider>
        <Toolbar />
        {!isLoginModalOpen && <ModeToast />}
        <FloorPlannerScene />
      </SingleSelectProvider>
      <DrawerContainer />
      <CompassWidget />
      <ScaleWidget />
      <LogoContainer />
    </>
  );
};

const App = () => {
  const { locale } = useLanguage();
  const [messages, setMessages] = useState({});
  const [showIntro, setShowIntro] = useState(true);

  useEffect(() => {
    const loadLocaleData = async () => {
      const intl = await createIntlInstance(locale);
      setMessages(intl.messages);
    };
    loadLocaleData();
  }, [locale]);

  const handleIntroEnd = () => {
    setShowIntro(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <IntlProvider defaultLocale={'de'} locale={locale} messages={messages} onError={intlErrorHandler}>
        <LoadingProvider>
          <ExportProvider>
            <ValidationProvider>
              <UnitProvider>
                <SnackbarProvider>
                  <ConfirmationDialogProvider>
                    <DraggingProvider>
                      <HistoryProvider>
                        <ToolbarProvider>
                          <DrawingProvider>
                            <LoginProvider>
                              <UserDataProvider>
                                <SelectionProvider>
                                  <MultiSelectToggleProvider>
                                    <ModalProvider>
                                      <PlanProvider>
                                        <RoomComponentsDataProvider>
                                          <AnnotationsProvider>
                                            <QueryParamsProvider>
                                              <BuildingProvider>
                                                <KeyboardShortcutProvider>
                                                  <SceneDataProvider>
                                                    <ZoomChangeProvider>
                                                      <AppContent showIntro={showIntro} handleIntroEnd={handleIntroEnd} />
                                                    </ZoomChangeProvider>
                                                  </SceneDataProvider>
                                                </KeyboardShortcutProvider>
                                              </BuildingProvider>
                                            </QueryParamsProvider>
                                          </AnnotationsProvider>
                                        </RoomComponentsDataProvider>
                                      </PlanProvider>
                                    </ModalProvider>
                                  </MultiSelectToggleProvider>
                                </SelectionProvider>
                              </UserDataProvider>
                            </LoginProvider>
                          </DrawingProvider>
                        </ToolbarProvider>
                      </HistoryProvider>
                    </DraggingProvider>
                  </ConfirmationDialogProvider>
                </SnackbarProvider>
              </UnitProvider>
            </ValidationProvider>
          </ExportProvider>
        </LoadingProvider>
      </IntlProvider>
    </ThemeProvider>
  );
};

export default App;
